import { useEffect, useRef } from 'react';
import { dismissActiveSizerPopups } from 'hooks/usePopupKeys';

/**
 * Phase 4d — the `.popup-wrapper` BACKDROP branch of the sizer's blanket body
 * click handler, `django/sizer/static/sizer/mvc/view/main.js:161-167`:
 *
 * ```js
 * $('body').on('click', function(event) {
 *   if ($('.popup-wrapper.active').length < 1) return;                       // :162
 *   const $target = $(event.target);
 *   const isInsidePopup = $target.closest('.popup-wrapper').length
 *                      && !$target.closest('.popup-content, .ui-widget-content').length; // :164
 *   if (isInsidePopup) { $('.popup-wrapper.active').removeClass('active'); } // :165-166
 *   … // :168-171 — the multiselect branch, NOT here (see below)
 * });
 * ```
 *
 * `isInsidePopup` is MISNAMED: it is true for a click on the BACKDROP — inside
 * the fixed `.popup-wrapper` overlay but outside its `.popup-content` card. So
 * this is "click the dimmed area to dismiss", and like the Esc branch it
 * dismisses EVERY active popup, not just the one clicked.
 *
 * ★ THE OTHER HALF IS NOT HERE. `main.js:168-171` (close the allowable-filter-
 * sizes multiselect on an outside click) is owned by
 * `components/AllowableFilterSizes.tsx`, which Builder 1 landed with its own
 * document listener scoped to that widget. Implementing it twice would close the
 * list on the first handler and then re-evaluate on the second.
 *
 * Dismissal goes through `dismissActiveSizerPopups`, shared with the Esc branch
 * — including the decision about the header Ratings popup's persisted state.
 *
 * ★ Bound to `document`, not `body`, and — like legacy — never re-bound: legacy
 * binds once in `initialize` (which runs once) and never unbinds. Here the
 * listener is torn down on unmount, because a React route change really can
 * leave the sizer.
 *
 * ★ 4e — `onSelectionPopupBackdrop`. The selection popup
 * (`.sizerSelection.popup-wrapper`) is dismissed differently from every other
 * popup on the page: the blanket handler only strips its `.active`, and its own
 * state machine stays at `opened` (full rationale on `dismissActiveSizerPopups`'
 * `excludeSelector` parameter). It is therefore excluded from the shared
 * dismissal and routed to the controller instead. Everything else — the settings
 * popup, more-notes, the header Ratings popup — behaves exactly as before.
 */
export function useSizerBackdropClick(
  options: { onSelectionPopupBackdrop?: () => void } = {}
): void {
  const { onSelectionPopupBackdrop } = options;
  const handlerRef = useRef(onSelectionPopupBackdrop);
  handlerRef.current = onSelectionPopupBackdrop;

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target || typeof target.closest !== 'function') return;
      // :162 — nothing open, nothing to do.
      if (!document.querySelector('.popup-wrapper.active')) return;
      const insideWrapper = target.closest('.popup-wrapper');
      const insideContent = target.closest('.popup-content, .ui-widget-content');
      if (!insideWrapper || insideContent) return; // :164
      dismissActiveSizerPopups('.sizerSelection'); // :165-166
      if (document.querySelector('.sizerSelection.popup-wrapper.active')) {
        handlerRef.current?.();
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
}
